import "#legacy/shared/i18n.js";

import.meta.glob("./utils/**/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./models/**/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./collections/**/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./views/**/*.{js,hbs,jst.ejs}", { eager: true });
import.meta.glob("./routers/**/*.{js,hbs,jst.ejs}", { eager: true });

window.Sparkle ??= {};
window.Sparkle.LocationBrowser ??= {};

// Under Vite, the `import.meta.glob({eager: true})` calls at the top of this
// file load `./collections/**`, `./models/**`, `./views/**`, `./routers/**`
// BEFORE this file's body runs. Those modules attach to
// `Sparkle.LocationBrowser.{Collections,Models,Views,Routers}` via `??= {}`.
// We preserve those attachments here so that `new Sparkle.LocationBrowser(...)`
// can reach `Sparkle.LocationBrowser.Collections.Locations` etc. Under Sprockets
// this was safe because `index.js` was concatenated first.
const __prevLocationBrowserNamespace = window.Sparkle.LocationBrowser;

window.Sparkle.LocationBrowser = class LocationBrowser {
  static Collections = {};
  static Models = {};
  static Routers = {};
  static Views = {};
  static instance = null;

  // The catalog SparkleConfig carries for the page's locale. The container's
  // `data-i18n` (rendered by locations_helper) merges over it, so an embedded
  // browser on a host page that carries no SparkleConfig still shows words.
  static defaultI18n() {
    return window.SparkleConfig?.i18n?.locationBrowser ?? {};
  }

  // Runtime string lookup for the widget's views. The browser is a singleton
  // (the constructor enforces it), so the instance's catalog is unambiguous.
  static t(key) {
    return this.instance?.i18n?.t(key);
  }

  // The options the browser starts `Backbone.history` with. Routes live in the hash, so the root only has
  // to be the page the browser is on: `/locations`, `/es/locations`, or a host CMS page. Decoded, as
  // Backbone decodes the path it compares with it.
  static historyOptions({ silent = false } = {}) {
    return {
      pushState: false,
      root: Backbone.history.decodeFragment(window.location.pathname),
      silent,
    };
  }

  constructor(container) {
    this.changeParentParams = this.changeParentParams.bind(this);
    this.clearSearchParams = this.clearSearchParams.bind(this);
    this.findClosest = this.findClosest.bind(this);
    this.setSearchParams = this.setSearchParams.bind(this);
    this.updateUrl = this.updateUrl.bind(this);
    this.renderLocationBrowser = this.renderLocationBrowser.bind(this);
    this.container = jQuery(container);
    if (!this.container.length) {
      throw "LocationBrowser container not found";
    }
    if (this.constructor.instance) {
      throw "The LocationBrowser is already running";
    }
    this.constructor.instance = this;

    this.i18n = new Sparkle.I18n(this.constructor.defaultI18n()).merge(this.container.data("i18n") || {});

    this.navigationEl = this.container.find(".lb-nav");
    this.searchInput = this.container.find(".lb-search input");

    this.locations = new Sparkle.LocationBrowser.Collections.Locations();
    this.mapView = new Sparkle.LocationBrowser.Views.MapCanvas({ collection: this.locations, nav: this.navigationEl });
    this.noResultsView = new Sparkle.LocationBrowser.Views.LocationNoResults({ collection: this.locations });

    _.extend(this, Backbone.Events);

    // BEHAVIOUR

    this.container.find("#grid-mobile-show-button").on("click", (event) => {
      event.preventDefault();
      this.container.find(".grid-mobile-show").hide();
      return this.container.find(".location-group").show();
    });

    this.noResultsView.on("resetClick", this.clearSearchParams);
    this.noResultsView.on("findClosest", this.findClosest);

    // Trigger event to notify analytics listeners
    this.searchInput.on("input", function () {
      if (jQuery.trim(this.value != null)) {
        return jQuery(document).trigger("autocomplete.sparkle", ["location-browser", this.value]);
      }
    });

    // INITIALIZE

    jQuery.when(Sparkle.GoogleMaps.load(), this.locations.fetch()).done(() => {
      new Sparkle.LocationBrowser.Routers.MapRouter(this.container[0], this.locations, {
        results: this.renderLocationBrowser,
        noResults: () => {
          if (this.searchInput != null) {
            this.searchInput.blur();
          }
          return this.container.append(this.noResultsView.render().el);
        },
        reset: this.renderLocationBrowser,
      });
      // The embedded-layout head shim stripped any router-style hash
      // (e.g. `#find/<slug>/focus`) so the host CMS's scroll handler
      // wouldn't crash trying to use it as a CSS selector. We do NOT
      // restore the hash to the URL — the host CMS binds setScrollToLocation
      // to `window.onload`, which fires AFTER our async fetchTemplate
      // finishes, so any hash present at that point trips the same crash.
      // Instead, we start Backbone silently, then drive the route by hand
      // using the captured fragment.
      const initialHash = window.__sparkleInitialHash;
      delete window.__sparkleInitialHash;

      Backbone.history.start(this.constructor.historyOptions({ silent: !!initialHash }));

      if (initialHash) {
        const fragment = initialHash.replace(/^#/, "");
        Backbone.history.loadUrl(fragment);
      }
    });
  }

  changeParentParams(parentParams) {
    if (parentParams != null ? parentParams.url : undefined) {
      return this.updateUrl({ parentParams });
    } else if (this.locations.flattened()) {
      return this.updateUrl({ parentParams: { id: null } });
    } else {
      return this.updateUrl({ parentParams });
    }
  }

  clearSearchParams() {
    this.locations.changeSearchParams({});
    return this.updateUrl({ searchParams: null });
  }

  findClosest() {
    return this.updateUrl({
      searchParams: {
        ...this.locations.searchParams,
        range: null,
      },
    });
  }

  setSearchParams(searchParams) {
    return this.updateUrl({ searchParams });
  }

  updateUrl({ parentParams, searchParams }) {
    let path;
    let parentPath = "";
    let searchPath = "";

    if (parentParams != null) {
      parentPath = (() => {
        switch (false) {
          case (parentParams != null ? parentParams.url : undefined) == null:
            return parentParams.url;
          case !(parentParams != null ? parentParams.hasOwnProperty("id") : undefined):
            return __guard__(this.locations.get(parentParams.id), (x) => x.get("url")) || "";
          default:
            return (this.locations.currentParent != null ? this.locations.currentParent.get("url") : undefined) || "";
        }
      })();

      parentPath = Sparkle.LocationBrowser.Collections.Locations.barePath(parentPath).replace(/\/locations\/?/, "");
    }

    searchParams = searchParams || this.locations.searchParams;
    if (searchParams != null) {
      const { name, lat, lng, range, zip, state } = searchParams;
      if (zip) {
        searchPath = `zip/${zip}`;
      } else if (state) {
        parentPath = state.toLowerCase();
      } else if (lat && lng && name) {
        searchPath = "coordinates/";
        searchPath += encodeURI(name.replace(/, /g, "_").replace(/ /g, "\u2014"));
        searchPath += `/${Number(lat).toFixed(5)}`;
        searchPath += `/${Number(lng).toFixed(5)}`;
        searchPath += `/${range || "closest"}`;
      }
    }

    if (parentPath && searchPath) {
      path = `find/${searchPath}/${parentPath}/focus`;
    } else if (parentPath) {
      path = `find/${parentPath}/focus`;
    } else if (searchPath) {
      path = `find/${searchPath}`;
    }

    return Backbone.history.navigate(path, true);
  }

  renderLocationBrowser() {
    this.navigationEl.addClass("initialized");

    const props = {
      root: this.locations.currentParent != null ? this.locations.currentParent.toJSON() : undefined,
      locations: this.locations.currentScope().toJSON(),
      searchActive: !_.isEmpty(this.locations.searchParams),
      searchText: (this.locations.searchParams != null ? this.locations.searchParams.name : undefined) || "",
      lat: this.locations.searchParams != null ? this.locations.searchParams.lat : undefined,
      lng: this.locations.searchParams != null ? this.locations.searchParams.lng : undefined,
      name: this.locations.searchParams != null ? this.locations.searchParams.name : undefined,
      distance: (this.locations.searchParams != null ? this.locations.searchParams.range : undefined) || "",
    };

    if (this.navigationView) {
      return this.navigationView.update(props);
    } else {
      this.navigationView = new Sparkle.LocationBrowser.Views.Navigation(props);
      return this.navigationEl.empty().append(this.navigationView.render().el);
    }
  }
};

for (const bucketKey of ["Collections", "Models", "Routers", "Views"]) {
  const prior = __prevLocationBrowserNamespace[bucketKey];
  if (prior && typeof prior === "object") {
    Object.assign(window.Sparkle.LocationBrowser[bucketKey], prior);
  }
}

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null ? transform(value) : undefined;
}
